import React, {useMemo, useState} from "react";
import {Box, Paper, Stack, Typography, List, ListItemButton, ListSubheader} from "@mui/material";
import {Identity, HelpPopover} from "./cells";
import MiniBars from "./MiniBars";
import {fmtMoneyShort, fmtPct0, fmtDay, EMPTY, isNilValue} from "./format";
import {TYPE, TABULAR, NEUTRAL, WARNING_TEXT, ATTENTION_ROW_H} from "./tokens";

export const LAUNCHES_HELP = [
    "A launch is a tag whose first earning day is within the last 7 days.",
    "Pace = partner coverage (searches counted ÷ searches sent) on the last reported day vs day 1; 'too early' while only one day is reported.",
    "Lesson from se67: coverage halved on day 2 and the partner stopped paying the day after — a falling pace is the early warning.",
    "Click a row for the tag's details.",
];

const BARS_W = 88;
const PHONE_BARS_W = 61;
const SUB_H = 28;

const PACE_LABEL = {true: "on pace", false: "off pace", null: "too early"};
/** `too early` only while a second reported day is missing; without coverage at all the honest label is `no coverage data`. */
const paceLabel = (l) => (l.pace_ok === null && !(l.daily && l.daily[0] && !isNilValue(l.daily[0].coverage)) ? "no coverage data" : PACE_LABEL[String(l.pace_ok)]);

const keyActivate = (fn) => (e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); fn(); } };

/** Off-pace launches first, then the biggest day 1 (the most money to lose). */
export const sortLaunches = (launches) => [...launches].sort((a, b) => {
    const ao = a.pace_ok === false ? 0 : 1;
    const bo = b.pace_ok === false ? 0 : 1;
    return (ao - bo) || ((Number(b.day1_rev) || 0) - (Number(a.day1_rev) || 0));
});

/** Day-1 coverage → coverage on the last day the partner actually reported (null when none). */
const coverageEnds = (daily) => {
    const days = Array.isArray(daily) ? daily : [];
    const first = days.length ? days[0].coverage : null;
    const reported = days.filter((d) => d.reported);
    const last = reported.length ? reported[reported.length - 1].coverage : null;
    return {first: isNilValue(first) ? null : Number(first), last: isNilValue(last) ? null : Number(last)};
};

const paceText = (l) => `day-1 pace ${fmtMoneyShort(l.day1_rev)}/d → ${fmtMoneyShort(l.last_rev)}`;
const coverageText = (l) => {
    const c = coverageEnds(l.daily);
    const reported = (l.daily || []).filter((d) => d.reported !== false);
    const lastDay = reported.length ? reported[reported.length - 1] : null;
    const lastText = c.last === null ? (lastDay && Number(lastDay.fs) === 0 ? "nothing sent" : EMPTY) : fmtPct0(c.last);
    return `coverage ${c.first === null ? EMPTY : fmtPct0(c.first)} → ${lastText}`;
};
const PENDING_TEXT = "◌ last day not yet reported";
const sinceText = (l) => `since ${fmtDay(l.first_day)}`;
const dayText = (l) => `day ${isNilValue(l.day_n) ? EMPTY : Number(l.day_n)}`;

const paceLabelSx = (paceOk) => ({
    fontSize: 12, whiteSpace: "nowrap",
    color: paceOk === false ? WARNING_TEXT : "text.secondary",
    fontWeight: paceOk === false ? 600 : 400,
});

/** Tiny text marker for a launch that is also in the attention feed (no status here, so no StatusChip). */
const InAlarm = () => (
    <Box component="span" sx={{fontSize: 10.5, fontWeight: 700, letterSpacing: "0.02em", color: WARNING_TEXT, ml: 0.75, whiteSpace: "nowrap"}}>in alarm</Box>
);

const rowSx = (selected) => ({
    display: "grid", gridTemplateColumns: `44px minmax(0,1fr) 52px 88px ${BARS_W + 62}px`, gridTemplateRows: "20px 18px",
    alignItems: "center", columnGap: 1, height: ATTENTION_ROW_H, px: 1.5, cursor: "pointer", boxSizing: "border-box",
    borderBottom: `1px solid ${NEUTRAL.hairline}`,
    bgcolor: selected ? NEUTRAL.selected : "transparent",
    "&:hover": {bgcolor: NEUTRAL.hover},
    "&:focus-visible": {outline: "none", boxShadow: NEUTRAL.focusRing},
});

/** One launch, two lines: tag · site · partner / pace → coverage; day N + since; pace label; coverage bars span both lines. */
const LaunchRow = ({l, selected, onSelect, inAlarm}) => {
    const line2 = `${paceText(l)} · ${coverageText(l)}${l.pending ? ` · ${PENDING_TEXT}` : ""}`;
    return (
        <Box role="button" tabIndex={0} aria-label={`${l.tag} launch, ${paceLabel(l)}, open details`}
             onClick={() => onSelect(l.tag)} onKeyDown={keyActivate(() => onSelect(l.tag))} sx={rowSx(selected)}>
            <Box sx={{gridRow: 1, gridColumn: 1, display: "flex", alignItems: "center", minWidth: 0}}>
                <Typography component="span" sx={{...TYPE.tag}}>{l.tag}</Typography>
            </Box>
            <Box sx={{gridRow: 1, gridColumn: 2, display: "flex", alignItems: "center", minWidth: 0}}>
                <Identity site={l.site} provider={l.provider}/>
                {inAlarm && <InAlarm/>}
            </Box>
            <Typography component="span" noWrap title={line2} sx={{gridRow: 2, gridColumn: 2, fontSize: 12, color: "text.secondary", minWidth: 0, ...TABULAR}}>
                day-1 pace {fmtMoneyShort(l.day1_rev)}/d → <Box component="span" sx={{color: "text.primary"}}>{fmtMoneyShort(l.last_rev)}</Box>
                <Box component="span" sx={{color: l.pace_ok === false ? WARNING_TEXT : "text.secondary"}}> · {coverageText(l)}</Box>
                {l.pending && <Box component="span"> · {PENDING_TEXT}</Box>}
            </Typography>
            <Typography component="span" sx={{gridRow: 1, gridColumn: 3, fontSize: 12, fontWeight: 600, ...TABULAR, color: "text.primary", whiteSpace: "nowrap"}}>
                {dayText(l)}
            </Typography>
            <Typography component="span" sx={{gridRow: 2, gridColumn: 3, fontSize: 11, color: "text.secondary", ...TABULAR, whiteSpace: "nowrap"}}>
                {sinceText(l)}
            </Typography>
            <Typography component="span" sx={{gridRow: 1, gridColumn: 4, ...paceLabelSx(l.pace_ok)}}>
                {paceLabel(l)}
            </Typography>
            <Box sx={{gridRow: "1 / 3", gridColumn: 5, display: "flex", alignItems: "center", justifyContent: "flex-end"}}>
                {/* One day = one solid bar that reads as 100 %: no sparkline until day 2. */}
                {Array.isArray(l.daily) && l.daily.length > 1 && <MiniBars daily={l.daily} metric="coverage" width={BARS_W} height={18} gap={2} partialLast={l.pending} label={`${l.tag}: daily coverage`}/>}
            </Box>
        </Box>
    );
};

/** Phone card: tag · site … day N / bars + pace / coverage · pace label. */
const LaunchCard = ({l, selected, onSelect, inAlarm}) => {
    const off = l.pace_ok === false;
    return (
        <ListItemButton divider selected={selected} onClick={() => onSelect(l.tag)}
                        aria-label={`${l.tag} launch, ${paceLabel(l)}, open details`}
                        sx={{display: "block", minHeight: 72, py: 1, px: 1.25, "&.Mui-selected": {bgcolor: NEUTRAL.selected}}}>
            <Box sx={{display: "flex", alignItems: "baseline", gap: 1, minWidth: 0}}>
                <Typography component="span" sx={{...TYPE.tag, fontSize: 14, flex: "0 0 auto"}}>{l.tag}</Typography>
                {inAlarm && <InAlarm/>}
                <Typography component="span" noWrap sx={{fontSize: 13, color: "text.secondary", flex: "1 1 auto", minWidth: 0}}>{l.site || EMPTY}</Typography>
                <Typography component="span" sx={{fontSize: 12, fontWeight: 600, ...TABULAR, flex: "0 0 auto"}}>{dayText(l)}</Typography>
            </Box>
            <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 0.5, minWidth: 0}}>
                {Array.isArray(l.daily) && l.daily.length > 1 && <MiniBars daily={l.daily} metric="coverage" width={PHONE_BARS_W} height={12} gap={2} partialLast={l.pending} label={`${l.tag}: daily coverage`}/>}
                <Typography component="span" noWrap sx={{fontSize: 12, color: "text.secondary", flex: "1 1 auto", minWidth: 0, ...TABULAR}} title={paceText(l)}>
                    pace {fmtMoneyShort(l.day1_rev)}/d → <Box component="span" sx={{color: "text.primary"}}>{fmtMoneyShort(l.last_rev)}</Box>
                </Typography>
                <Typography component="span" sx={{fontSize: 11, color: "text.secondary", flex: "0 0 auto", ...TABULAR}}>{sinceText(l)}</Typography>
            </Box>
            <Typography component="div" noWrap sx={{fontSize: 12, mt: 0.5, color: off ? WARNING_TEXT : "text.secondary", ...TABULAR}}
                        title={`${coverageText(l)} · ${paceLabel(l)}${l.pending ? ` · ${PENDING_TEXT}` : ""}`}>
                {coverageText(l)} · <Box component="span" sx={{fontWeight: off ? 600 : 400}}>{paceLabel(l)}</Box>
                {l.pending && ` · ${PENDING_TEXT}`}
            </Typography>
        </ListItemButton>
    );
};

// ---------------------------------------------------------------------------------------------------------------------

/**
 * Launches: tags that started earning within the last 7 days, watched by coverage pace (the se67 early warning) rather
 * than revenue. Off-pace first. Renders nothing when there is no launch.
 */
const LaunchesPanel = ({launches, onSelect, selectedTag = null, phone = false, alarmTags = null}) => {
    const rows = useMemo(() => sortLaunches(Array.isArray(launches) ? launches : []), [launches]);
    const [showAlarm, setShowAlarm] = useState(false);
    if (!rows.length) return null;
    const offPace = rows.filter((l) => l.pace_ok === false).length;
    const earliest = rows.map((l) => l.first_day).filter(Boolean).sort()[0];
    const isAlarm = (tag) => Boolean(alarmTags && typeof alarmTags.has === "function" && alarmTags.has(tag));

    if (phone) {
        return (
            <List disablePadding>
                <ListSubheader disableSticky component="div" sx={{
                    height: SUB_H, lineHeight: `${SUB_H}px`, px: 1.5, fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em",
                    color: "text.secondary", bgcolor: NEUTRAL.headerBg, borderBottom: `1px solid ${NEUTRAL.hairline}`, ...TABULAR,
                }}>
                    Launches · {rows.length}{offPace > 0 && <Box component="span" sx={{color: WARNING_TEXT}}> · {offPace} off pace</Box>}
                </ListSubheader>
                {rows.filter((l) => !isAlarm(l.tag)).map((l) => <LaunchCard key={l.tag} l={l} selected={selectedTag === l.tag} onSelect={onSelect} inAlarm={false}/>)}
                {rows.some((l) => isAlarm(l.tag)) && (
                    <ListItemButton divider onClick={() => setShowAlarm((v) => !v)} aria-expanded={showAlarm} sx={{minHeight: 40, py: 0.5, px: 1.5}}>
                        <Typography component="span" sx={{fontSize: 12, color: "text.secondary", ...TABULAR, flex: "1 1 auto", minWidth: 0}} noWrap>
                            {rows.filter((l) => isAlarm(l.tag)).length} already in alarm (see What happened) · {rows.filter((l) => isAlarm(l.tag)).map((l) => l.tag).join(" ")}
                        </Typography>
                        <Typography component="span" sx={{fontSize: 12, color: "primary.main", fontWeight: 600, flex: "0 0 auto"}}>{showAlarm ? "hide" : "show"}</Typography>
                    </ListItemButton>
                )}
                {showAlarm && rows.filter((l) => isAlarm(l.tag)).map((l) => <LaunchCard key={l.tag} l={l} selected={selectedTag === l.tag} onSelect={onSelect} inAlarm/>)}
            </List>
        );
    }

    return (
        <Paper variant="outlined" sx={{borderRadius: 1.5, p: 1.5, pt: 1.25, display: "flex", flexDirection: "column", minWidth: 0}}>
            <Stack direction="row" alignItems="center" flexWrap="wrap" sx={{columnGap: 1, rowGap: 0.5, mb: 0.75}}>
                <Typography component="h2" sx={{...TYPE.eyebrow, textTransform: "uppercase", letterSpacing: "0.04em"}}>Launches</Typography>
                <Typography component="span" sx={{fontSize: 13, fontWeight: 700, ...TABULAR, color: "text.secondary"}}>{rows.length}</Typography>
                {offPace > 0 && (
                    <Typography component="span" sx={{fontSize: 12, color: WARNING_TEXT, ...TABULAR, whiteSpace: "nowrap"}}>· {offPace} off pace</Typography>
                )}
                {earliest && (
                    <Typography component="span" sx={{fontSize: 12, color: "text.secondary", ...TABULAR, whiteSpace: "nowrap"}}>· since {fmtDay(earliest)}</Typography>
                )}
                <Box sx={{flexGrow: 1}}/>
                <HelpPopover label="Launches" lines={LAUNCHES_HELP}/>
            </Stack>
            <Box sx={{mx: -1.5, borderTop: `1px solid ${NEUTRAL.hairline}`}}>
                {rows.filter((l) => !isAlarm(l.tag)).map((l) => <LaunchRow key={l.tag} l={l} selected={selectedTag === l.tag} onSelect={onSelect} inAlarm={false}/>)}
                {rows.some((l) => isAlarm(l.tag)) && (
                    <Box role="button" tabIndex={0} aria-expanded={showAlarm} onClick={() => setShowAlarm((v) => !v)}
                         onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); setShowAlarm((v) => !v); } }}
                         sx={{display: "flex", alignItems: "center", gap: 1, height: 30, px: 1.5, cursor: "pointer", borderBottom: `1px solid ${NEUTRAL.hairline}`, "&:hover": {bgcolor: NEUTRAL.hover}, "&:focus-visible": {outline: "none", boxShadow: NEUTRAL.focusRing}}}>
                        <Typography component="span" sx={{fontSize: 12, color: "text.secondary", ...TABULAR}}>
                            {rows.filter((l) => isAlarm(l.tag)).length} launch{rows.filter((l) => isAlarm(l.tag)).length === 1 ? "" : "es"} already in alarm (see What happened) · {rows.filter((l) => isAlarm(l.tag)).map((l) => l.tag).join(" ")}
                        </Typography>
                        <Box sx={{flex: "1 1 auto"}}/>
                        <Typography component="span" sx={{fontSize: 12, color: "primary.main", fontWeight: 600}}>{showAlarm ? "hide" : "show"}</Typography>
                    </Box>
                )}
                {showAlarm && rows.filter((l) => isAlarm(l.tag)).map((l) => <LaunchRow key={l.tag} l={l} selected={selectedTag === l.tag} onSelect={onSelect} inAlarm/>)}
            </Box>
        </Paper>
    );
};

export default LaunchesPanel;
